import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/** How many discount codes the cart offers to hold at once. */
const MAX_DISCOUNT_CODES = 2;

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (section) {
        const codes = this.#collectDiscountCodes(section);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      this.#syncInputFromSection(section);
      morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event on the remove control.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    // Matches both the inline "Entfernen" button next to the field and the pills
    // listing any additional codes — whichever carries the code is the trigger.
    const trigger = event.target.closest('[data-discount-code]');
    if (!(trigger instanceof HTMLElement)) return;

    const discountCode = trigger.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      this.#syncInputFromSection(parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`));

      morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Hands the field back to the customer without dropping the code it was holding.
   *
   * The code moves down into the list of pills, where it stays visible, stays removable and —
   * crucially — stays part of #existingDiscounts(), so the next submit sends both codes rather
   * than replacing the first one. Nothing is sent to the cart here; this is purely the UI
   * making room for a second code.
   *
   * @param {MouseEvent | KeyboardEvent} event - The event on the "Weiteren Code hinzufügen" control.
   */
  addAnotherDiscount = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event instanceof KeyboardEvent && event.key !== 'Enter') return;

    // Liquid already hides the control at the limit; this guards the case where it didn't,
    // so the field can never be opened for a code the cart wouldn't accept.
    if (this.#existingDiscounts().length >= MAX_DISCOUNT_CODES) return;

    const input = this.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;

    const appliedCode = input.dataset.appliedCode;
    // The badge beside the field states what this code alone saves; it travels with the code so
    // the pill shows the same figure the server would have rendered for it.
    const savingLabel = this.querySelector('.cart-discount__percent')?.textContent?.trim() ?? '';
    if (appliedCode) this.#appendPill(appliedCode, savingLabel);

    input.value = '';
    input.dataset.appliedCode = '';
    input.removeAttribute('readonly');
    this.querySelector('.cart-discount__field')?.classList.remove('cart-discount__field--active');
    input.focus();
  };

  /**
   * Adds one more pill for an already applied code, cloned from the Liquid-rendered template so
   * it carries the same markup, icon and hooks as a server-rendered pill.
   *
   * @param {string} code - The discount code the pill stands for.
   * @param {string} [savingLabel] - What this code saves, e.g. "-15%". Omitted when it saves nothing.
   */
  #appendPill(code, savingLabel = '') {
    const list = this.querySelector('.cart-discount__codes');
    const template = this.querySelector('template[data-discount-pill-template]');
    if (!(list instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;
    if (list.querySelector(`[data-discount-code="${CSS.escape(code)}"]`)) return;

    const pill = template.content.firstElementChild?.cloneNode(true);
    if (!(pill instanceof HTMLElement)) return;

    pill.dataset.discountCode = code;
    pill.setAttribute('aria-label', `Aktionscode ${code} angewendet`);

    const label = pill.querySelector('.cart-discount__pill-code');
    if (label) label.textContent = code;

    const saving = pill.querySelector('.cart-discount__pill-percent');
    if (saving && savingLabel) {
      saving.textContent = savingLabel;
      saving.hidden = false;
    }

    pill.querySelector('.cart-discount__pill-remove')?.setAttribute('aria-label', `Aktionscode ${code} entfernen`);

    list.appendChild(pill);
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Copies the server-rendered state of the discount field onto the live input.
   *
   * The input carries data-skip-node-update so morphSection deliberately leaves it
   * untouched — which also means its value, readonly flag and data-applied-code would
   * otherwise keep showing the pre-request state. Copy them across by hand instead.
   *
   * @param {HTMLElement | null} section - The freshly rendered section, already parsed.
   */
  #syncInputFromSection(section) {
    const input = this.querySelector('input[name="discount"]');
    const serverInput = section?.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement) || !(serverInput instanceof HTMLInputElement)) return;

    input.value = serverInput.getAttribute('value') ?? '';
    input.dataset.appliedCode = serverInput.dataset.appliedCode ?? '';
    input.toggleAttribute('readonly', serverInput.hasAttribute('readonly'));
  }

  /**
   * Collects every discount code rendered inside a root element: the one held by the
   * input field plus any further codes shown as pills.
   *
   * @param {ParentNode} root - The element to read the rendered codes from.
   * @returns {string[]}
   */
  #collectDiscountCodes(root) {
    /** @type {string[]} */
    const discountCodes = [];

    const input = root.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement && input.dataset.appliedCode) {
      discountCodes.push(input.dataset.appliedCode);
    }

    for (const pill of root.querySelectorAll('.cart-discount__pill')) {
      if (pill instanceof HTMLElement && pill.dataset.discountCode) {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    return this.#collectDiscountCodes(this);
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
